// Pooled visual effects: billboard particles, shockwave rings, flash spheres, impact lights, lightning arcs.
import * as THREE from 'three/webgpu';
import type { BlastFx } from './blastFx';
import { BIG, FLASH, isWhiteish } from './a11y';

export function softDotTexture(): THREE.Texture {
  const c = document.createElement('canvas'); c.width = c.height = 64;
  const g = c.getContext('2d')!;
  const grd = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grd.addColorStop(0, 'rgba(255,255,255,1)'); grd.addColorStop(0.25, 'rgba(255,255,255,0.8)');
  grd.addColorStop(0.6, 'rgba(255,255,255,0.18)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd; g.fillRect(0, 0, 64, 64);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
}

/** Anime impact burst: a sharp 8-point star with a hot centre (drawn procedurally). */
export function starTexture(): THREE.Texture {
  const S = 128, c = document.createElement('canvas'); c.width = c.height = S;
  const g = c.getContext('2d')!; const h = S / 2;
  g.translate(h, h);
  const spikes = 8;
  g.beginPath();
  for (let i = 0; i < spikes * 2; i++) {
    const a = (i / (spikes * 2)) * Math.PI * 2 - Math.PI / 2;
    const r = i % 2 === 0 ? (i % 4 === 0 ? h * 0.98 : h * 0.62) : h * 0.16;
    g.lineTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  g.closePath();
  const grd = g.createRadialGradient(0, 0, 0, 0, 0, h);
  grd.addColorStop(0, 'rgba(255,255,255,1)'); grd.addColorStop(0.35, 'rgba(255,255,255,0.85)'); grd.addColorStop(1, 'rgba(255,255,255,0.15)');
  g.fillStyle = grd; g.fill();
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
}

/** Crystal shard: a thin bright diamond, used for "shatter" debris. */
export function shardTexture(): THREE.Texture {
  const c = document.createElement('canvas'); c.width = 32; c.height = 64;
  const g = c.getContext('2d')!;
  g.beginPath(); g.moveTo(16, 0); g.lineTo(27, 30); g.lineTo(16, 64); g.lineTo(5, 30); g.closePath();
  const grd = g.createLinearGradient(5, 0, 27, 0);
  grd.addColorStop(0, 'rgba(255,255,255,0.55)'); grd.addColorStop(0.5, 'rgba(255,255,255,1)'); grd.addColorStop(1, 'rgba(255,255,255,0.4)');
  g.fillStyle = grd; g.fill();
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
}

const additive = (map?: THREE.Texture, side: THREE.Side = THREE.FrontSide) => {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side, fog: false });
  if (map) m.map = map;
  return m;
};

/** "Max" blending: overlapping effects keep their own saturated colour instead of summing to white
 *  (anime-style readability in dense fights). Fade-to-black still reads as fade-out. */
const maxBlend = (map?: THREE.Texture, side: THREE.Side = THREE.FrontSide) => {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, blending: THREE.CustomBlending, depthWrite: false, side, fog: false });
  m.blendEquation = THREE.MaxEquation; m.blendSrc = THREE.OneFactor; m.blendDst = THREE.OneFactor;
  if (map) m.map = map;
  return m;
};

const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion(), _c = new THREE.Color();
const _zAxis = new THREE.Vector3(0, 0, 1); // PERF-STRESS: shared (was allocated per arc segment per frame)
const UP = new THREE.Vector3(0, 1, 0);

/** owner 2026-11-01: technique VFX were too bright — every additive skill effect is scaled to 25% (75% less bright).
 *  `core` is used for opaque cores (beam ribbon, projectile spheres), which only drop to 50% so they stay readable. */
export const SKILL = { k: 0.25, core: 0.5 };
/** Crowding governor (owner photo 2026-11-01: late fights washed the screen out). busy 0..1 is how full the effect
 *  pools are; new effects get dimmer (down to 1/3) and sparser as it rises, and main lowers bloom with it. */
export const CROWD = { busy: 0 };
const crowdGain = () => 1 / (1 + 2 * CROWD.busy);

export class Particles {
  readonly mesh: THREE.InstancedMesh;
  /** Surge/hero aura emission sets this so the 75% skill dimming does not apply to it */
  exempt = false;
  private n = 0;
  private cap: number;
  private d: Float32Array; // x y z vx vy vz life max size r g b drag grav
  private static S = 14;
  constructor(cap: number, tex: THREE.Texture) {
    this.cap = cap;
    this.d = new Float32Array(cap * Particles.S);
    this.mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), maxBlend(tex, THREE.DoubleSide), cap);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
  }
  emit(x: number, y: number, z: number, count: number, speed: number, col: number, size: number, life: number, up = 0, grav = 0, drag = 2.5, intensity = 2): void {
    if (!this.exempt && CROWD.busy > 0.4) count = Math.max(1, Math.round(count * (1.4 - CROWD.busy)));
    _c.setHex(col).multiplyScalar(intensity * (this.exempt ? 1 : SKILL.k * crowdGain()));
    for (let k = 0; k < count; k++) {
      if (this.n >= this.cap) this.kill(Math.floor(Math.random() * this.n));
      const i = this.n++ * Particles.S, d = this.d;
      const th = Math.random() * Math.PI * 2, ph = Math.acos(2 * Math.random() - 1), sp = speed * (0.35 + Math.random() * 0.65);
      d[i] = x; d[i + 1] = y; d[i + 2] = z;
      d[i + 3] = Math.sin(ph) * Math.cos(th) * sp; d[i + 4] = Math.cos(ph) * sp * 0.6 + up; d[i + 5] = Math.sin(ph) * Math.sin(th) * sp;
      const l = life * (0.6 + Math.random() * 0.6);
      d[i + 6] = l; d[i + 7] = l; d[i + 8] = size * (0.6 + Math.random() * 0.8);
      d[i + 9] = _c.r; d[i + 10] = _c.g; d[i + 11] = _c.b; d[i + 12] = drag; d[i + 13] = grav;
    }
  }
  emitDir(x: number, y: number, z: number, vx: number, vy: number, vz: number, col: number, size: number, life: number, intensity = 2): void {
    if (this.n >= this.cap) return;
    _c.setHex(col).multiplyScalar(intensity * (this.exempt ? 1 : SKILL.k));
    const i = this.n++ * Particles.S, d = this.d;
    d[i] = x; d[i + 1] = y; d[i + 2] = z; d[i + 3] = vx; d[i + 4] = vy; d[i + 5] = vz;
    d[i + 6] = life; d[i + 7] = life; d[i + 8] = size; d[i + 9] = _c.r; d[i + 10] = _c.g; d[i + 11] = _c.b; d[i + 12] = 3; d[i + 13] = 0;
  }
  private kill(idx: number): void {
    const S = Particles.S; this.n--;
    if (idx !== this.n) this.d.copyWithin(idx * S, this.n * S, this.n * S + S);
  }
  get count(): number { return this.n; }
  get capacity(): number { return this.cap; }
  update(dt: number, camQ: THREE.Quaternion): void {
    const d = this.d, S = Particles.S;
    for (let k = 0; k < this.n; k++) {
      const i = k * S;
      d[i + 6] -= dt;
      if (d[i + 6] <= 0) { this.kill(k); k--; continue; }
      const dr = Math.exp(-d[i + 12] * dt);
      d[i + 3] *= dr; d[i + 4] = d[i + 4] * dr - d[i + 13] * dt; d[i + 5] *= dr;
      d[i] += d[i + 3] * dt; d[i + 1] += d[i + 4] * dt; d[i + 2] += d[i + 5] * dt;
      if (d[i + 1] < 0.05) { d[i + 1] = 0.05; d[i + 4] *= -0.3; }
    }
    const col = this.mesh.instanceColor!.array as Float32Array;
    for (let k = 0; k < this.n; k++) {
      const i = k * S;
      const t = d[i + 6] / d[i + 7];
      const sz = d[i + 8] * (0.4 + 0.6 * t);
      _p.set(d[i], d[i + 1], d[i + 2]); _s.set(sz, sz, sz);
      this.mesh.setMatrixAt(k, _m.compose(_p, camQ, _s));
      const f = t * t;
      col[k * 3] = d[i + 9] * f; col[k * 3 + 1] = d[i + 10] * f; col[k * 3 + 2] = d[i + 11] * f;
    }
    this.mesh.count = this.n;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mesh.instanceColor!.needsUpdate = true;
  }
  clear(): void { this.n = 0; this.mesh.count = 0; }
}

/** Generic short-lived scaled instances (rings, flash spheres). */
class Pulses {
  readonly mesh: THREE.InstancedMesh;
  private items: { x: number; y: number; z: number; r: number; t: number; life: number; c: THREE.Color; flat: boolean; thick: number }[] = [];
  constructor(geo: THREE.BufferGeometry, cap: number, private billboardGrow: 'ring' | 'sphere', private isFlash = false) {
    this.mesh = new THREE.InstancedMesh(geo, maxBlend(undefined, THREE.DoubleSide), cap);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    this.mesh.frustumCulled = false; this.mesh.count = 0;
  }
  add(x: number, y: number, z: number, r: number, life: number, col: number, intensity: number, thick = 1): void {
    if (this.isFlash) { intensity *= FLASH.gate(r >= BIG.sphere); if (intensity <= 0.001) return; } // a11y: flash spheres share the flash budget
    if (this.items.length >= this.mesh.instanceMatrix.count) this.items.shift();
    this.items.push({ x, y, z, r, t: 0, life, c: new THREE.Color(col).multiplyScalar(intensity * SKILL.k * crowdGain()), flat: true, thick });
  }
  get load(): number { return this.items.length / this.mesh.instanceMatrix.count; }
  update(dt: number): void {
    const col = this.mesh.instanceColor!.array as Float32Array;
    let n = 0;
    for (let k = 0; k < this.items.length; k++) {
      const it = this.items[k]; it.t += dt;
      if (it.t >= it.life) { this.items.splice(k, 1); k--; continue; }
      const u = it.t / it.life;
      const ease = 1 - Math.pow(1 - u, 3);
      const sc = it.r * (this.billboardGrow === 'ring' ? 0.15 + 0.85 * ease : 0.3 + 0.7 * ease);
      _p.set(it.x, it.y, it.z);
      _s.set(sc, this.billboardGrow === 'ring' ? sc : sc, sc);
      _q.identity();
      if (this.billboardGrow === 'ring') _s.set(sc, sc, sc * it.thick);
      this.mesh.setMatrixAt(n, _m.compose(_p, _q, _s));
      const f = (1 - u) * (1 - u);
      col[n * 3] = it.c.r * f; col[n * 3 + 1] = it.c.g * f; col[n * 3 + 2] = it.c.b * f;
      n++;
    }
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true; this.mesh.instanceColor!.needsUpdate = true;
  }
}

export class Arcs {
  readonly mesh: THREE.InstancedMesh;
  private segs: { a: THREE.Vector3; b: THREE.Vector3; t: number; life: number; w: number; r: number; g: number; bl: number }[] = [];
  private col = new THREE.Color(0x9fd8ff).multiplyScalar(2.2);
  constructor(cap: number) {
    const g = new THREE.BoxGeometry(1, 1, 1); g.translate(0, 0, 0.5);
    this.mesh = new THREE.InstancedMesh(g, additive(), cap);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    this.mesh.frustumCulled = false; this.mesh.count = 0;
  }
  /** width: thickness multiplier; life: seconds; jag: max sideways kink per subdivision (m). Defaults = Chain Arc look. */
  addChain(pts: number[], color?: number, width = 1, life = 0.16, jag = 1.2, intensity = 2.2): void {
    this.col.setHex(color ?? 0x9fd8ff).multiplyScalar(intensity * SKILL.k * FLASH.L.bolt); // per bolt (stored on each segment); a11y dims lightning
    for (let i = 0; i + 5 < pts.length; i += 3) {
      const a = new THREE.Vector3(pts[i], pts[i + 1] + 0.3, pts[i + 2]), b = new THREE.Vector3(pts[i + 3], pts[i + 4] + 0.3, pts[i + 5]);
      // jagged subdivision
      let prev = a.clone();
      const SUB = 4;
      for (let s = 1; s <= SUB; s++) {
        const p = a.clone().lerp(b, s / SUB);
        if (s < SUB) p.add(new THREE.Vector3((Math.random() - 0.5) * jag, (Math.random() - 0.5) * jag, (Math.random() - 0.5) * jag));
        if (this.segs.length < this.mesh.instanceMatrix.count) this.segs.push({ a: prev, b: p, t: 0, life, w: width, r: this.col.r, g: this.col.g, bl: this.col.b });
        prev = p;
      }
    }
  }
  update(dt: number): void {
    const col = this.mesh.instanceColor!.array as Float32Array;
    let n = 0;
    for (let k = 0; k < this.segs.length; k++) {
      const s = this.segs[k]; s.t += dt;
      if (s.t > s.life) { this.segs.splice(k, 1); k--; continue; }
      const dir = _p.copy(s.b).sub(s.a); const len = dir.length();
      _q.setFromUnitVectors(_zAxis, dir.normalize());
      const w = (0.12 * (1 - s.t / s.life) + 0.03) * s.w;
      _s.set(w, w, len);
      this.mesh.setMatrixAt(n, _m.compose(s.a, _q, _s));
      const f = 1 - s.t / s.life;
      col[n * 3] = s.r * f; col[n * 3 + 1] = s.g * f; col[n * 3 + 2] = s.bl * f;
      n++;
    }
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true; this.mesh.instanceColor!.needsUpdate = true;
  }
}

/** accessibility: the central flash limiter (src/render/a11y.ts) — re-exported for the modules that import it from here */
export { FLASH };
/** MAP SYSTEM night readability: skills light their surroundings more at night (set by render/timeOfDay.ts; 1 by day) */
export const LIGHT_GAIN = { k: 1 };

export class ImpactLights {
  private lights: { l: THREE.PointLight; t: number; life: number; i: number }[] = [];
  private next = 0;
  constructor(group: THREE.Object3D, n: number) {
    for (let i = 0; i < n; i++) {
      const l = new THREE.PointLight(0xffffff, 0, 16, 1.6);
      group.add(l); this.lights.push({ l, t: 1, life: 1, i: 0 });
    }
  }
  flash(x: number, y: number, z: number, col: number, intensity: number, life: number, range = 16): void {
    intensity *= FLASH.gate(intensity >= BIG.light) * SKILL.k * crowdGain() * LIGHT_GAIN.k; // a11y: big lights count against the flash budget
    if (this.lights.length === 0 || intensity <= 0.001) return;
    const s = this.lights[this.next]; this.next = (this.next + 1) % this.lights.length;
    s.l.position.set(x, y + 1, z); s.l.color.setHex(col); s.l.distance = range; s.t = 0; s.life = life; s.i = intensity;
  }
  update(dt: number): void {
    for (const s of this.lights) {
      s.t += dt; const u = Math.min(1, s.t / s.life);
      s.l.intensity = s.i * (1 - u) * (1 - u);
    }
  }
}

/** Camera-facing impact stars: pop in fast, spin a little, fade. */
export class Stars {
  readonly mesh: THREE.InstancedMesh;
  private items: { x: number; y: number; z: number; r: number; t: number; life: number; c: THREE.Color; rot: number }[] = [];
  private roll = new THREE.Quaternion(); private fwd = new THREE.Vector3(0, 0, 1);
  constructor(cap: number, tex: THREE.Texture) {
    this.mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), maxBlend(tex, THREE.DoubleSide), cap);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = 3;
  }
  add(x: number, y: number, z: number, r: number, life: number, col: number, intensity = 3): void {
    // a11y: white stars read as strobes, so they get an extra cut; big stars count against the flash budget
    const white = isWhiteish(col);
    intensity *= FLASH.gate(r >= (white ? BIG.whiteStar : BIG.star)) * (white ? FLASH.L.white : 1) * SKILL.k * crowdGain();
    if (intensity <= 0.001) return;
    if (this.items.length >= this.mesh.instanceMatrix.count) this.items.shift();
    this.items.push({ x, y, z, r, t: 0, life, c: new THREE.Color(col).multiplyScalar(intensity), rot: Math.random() * Math.PI });
  }
  update(dt: number, camQ: THREE.Quaternion): void {
    const col = this.mesh.instanceColor!.array as Float32Array;
    let n = 0;
    for (let k = 0; k < this.items.length; k++) {
      const it = this.items[k]; it.t += dt;
      if (it.t >= it.life) { this.items.splice(k, 1); k--; continue; }
      const u = it.t / it.life;
      const sc = it.r * (u < 0.25 ? 0.5 + 2 * u : 1 - (u - 0.25) * 0.6);   // pop then shrink
      this.roll.setFromAxisAngle(this.fwd, it.rot + u * 0.6);
      _q.copy(camQ).multiply(this.roll);
      _p.set(it.x, it.y, it.z); _s.set(sc, sc, sc);
      this.mesh.setMatrixAt(n, _m.compose(_p, _q, _s));
      const f = 1 - u * u;
      col[n * 3] = it.c.r * f; col[n * 3 + 1] = it.c.g * f; col[n * 3 + 2] = it.c.b * f;
      n++;
    }
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true; this.mesh.instanceColor!.needsUpdate = true;
  }
}

export class Fx {
  particles: Particles;
  shards: Particles;
  stars: Stars;
  rings: Pulses;
  flashes: Pulses;
  arcs: Arcs;
  lights: ImpactLights;
  /** smoke / dust / flying rock aftermath (set by the view) */
  blast: BlastFx | null = null;
  shake = 0;
  constructor(scene: THREE.Scene, maxParticles: number, lightCount: number, tex: THREE.Texture) {
    this.particles = new Particles(maxParticles, tex);
    this.shards = new Particles(Math.max(120, maxParticles >> 2), shardTexture());
    this.stars = new Stars(96, starTexture());
    const ringGeo = new THREE.RingGeometry(0.82, 1, 64).rotateX(-Math.PI / 2);
    this.rings = new Pulses(ringGeo, 64, 'ring');
    this.flashes = new Pulses(new THREE.SphereGeometry(1, 16, 12), 48, 'sphere', true);
    this.arcs = new Arcs(1200); // (was 400: Pulse Nova lightning adds many short arcs)
    this.lights = new ImpactLights(scene, lightCount);
    scene.add(this.particles.mesh, this.shards.mesh, this.stars.mesh, this.rings.mesh, this.flashes.mesh, this.arcs.mesh);
  }
  /** Crystal debris burst (kills, shatter hits). */
  shatter(x: number, y: number, z: number, n: number, speed: number, col: number, size = 0.5): void {
    this.shards.emit(x, y, z, n, speed, col, size, 0.7, speed * 0.5, 18, 1.2, 2.6);
  }
  explosion(x: number, y: number, z: number, r: number, col: number, big = false): void {
    // anime-style: saturated colour bands, only a small white-hot centre (less bloom wash)
    this.stars.add(x, y + 0.3, z, r * (big ? 1.5 : 1.1), big ? 0.24 : 0.14, 0xfff7ea, 1.25);
    this.stars.add(x, y + 0.3, z, r * (big ? 3 : 2.3), big ? 0.38 : 0.24, col, 1.15);
    this.flashes.add(x, y, z, r * 0.6, big ? 0.4 : 0.22, col, big ? 0.9 : 0.7);
    // second, slower shockwave + rising embers give the blast weight
    this.rings.add(x, 0.1, z, r * 2.1, big ? 1.0 : 0.6, col, 0.9, 1);
    this.particles.emit(x, y, z, Math.min(18, 4 + r * 2), r * 1.2, col, 0.35, 1.1, r * 1.6, -1.5, 1.2, 2.2);
    if (r >= 2) this.shatter(x, y, z, Math.min(16, 3 + r * 2), r * 3.5, col, 0.55);
    this.rings.add(x, Math.max(0.12, y * 0.15), z, r * 1.4, big ? 0.7 : 0.45, col, 3);
    this.particles.emit(x, y, z, Math.min(60, 10 + r * 6), r * 5, col, 0.6 + r * 0.08, 0.6, 2, 4);
    this.particles.emit(x, y, z, Math.min(20, 3 + r * 2), r * 2.5, 0xfff7ea, 0.9, 0.35, 1, 0, 3, 3);
    this.lights.flash(x, y, z, col, big ? 60 : 16 + r * 4, big ? 0.6 : 0.3, 10 + r * 3);
    this.shake = Math.min(1, this.shake + (big ? 0.6 : r * 0.04));
    this.blast?.blast(x, y, z, r, big, col);
    // energy sparkles glittering inside the dust (attack colour)
    if (r >= 3) for (let k = 0; k < Math.min(24, 6 + r * 2); k++) { const a = Math.random() * 6.283, d = Math.random() * r * 1.3; this.particles.emitDir(x + Math.cos(a) * d, 0.5 + Math.random() * r * 0.8, z + Math.sin(a) * d, (Math.random() - 0.5) * 3, 1 + Math.random() * 2, (Math.random() - 0.5) * 3, col, 0.18 + Math.random() * 0.15, 0.6 + Math.random() * 0.6, 4); }
  }
  update(dt: number, camQ: THREE.Quaternion): void {
    FLASH.tick(dt); // the flash budget runs on render time (frozen menus pass 0)
    this.particles.update(dt, camQ); this.shards.update(dt, camQ); this.stars.update(dt, camQ); this.rings.update(dt); this.flashes.update(dt); this.arcs.update(dt); this.lights.update(dt); this.blast?.update(dt, camQ);
    const want = Math.min(1, Math.max(this.particles.count / this.particles.capacity, this.rings.load, this.flashes.load * 0.9));
    CROWD.busy += (want - CROWD.busy) * Math.min(1, dt * 3); // smoothed so brightness doesn't flicker
    this.shake = Math.max(0, this.shake - dt * 2.6); // settles faster (owner 2026-11-02: 100 % shake was unbearable)
  }
}

export { UP };
